"use client"

import * as React from "react"
import { BrandCardList } from "./brand-card-list"
import type { IndexBrand } from "./types"

interface BrandSectionProps {
  /** Letter bucket (`A`–`Z` or `#`). */
  letter: string
  brands: IndexBrand[]
  /** Forwarded ref hook so the parent IntersectionObserver can track this
   *  section's position in the viewport for A–Z rail highlighting. */
  sectionRef: (el: HTMLElement | null) => void
}

/**
 * One letter-section block on the `/brands` index: a 36×36 dark letter
 * badge + count label + hairline, followed by a 3-col card grid of
 * `BrandCardList` entries.
 *
 * Logos view-mode toggle was removed in favour of always rendering the
 * list view — the bigger card with name + tier + price range is the
 * canonical browsing surface; the logo grid was a redundant alternative.
 */
export function BrandSection({ letter, brands, sectionRef }: BrandSectionProps) {
  if (brands.length === 0) return null
  const sectionId = `brands-${letter === "#" ? "num" : letter.toLowerCase()}`

  return (
    <section
      id={sectionId}
      data-letter={letter}
      ref={sectionRef as React.Ref<HTMLElement>}
      className="scroll-mt-[150px]"
    >
      <div className="my-10 flex items-center gap-3.5">
        <div className="grid h-9 w-9 place-items-center rounded-[10px] bg-gray-900 text-[16px] font-black tracking-[-0.02em] text-white">
          {letter}
        </div>
        <h2 className="text-[13px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {brands.length} {brands.length === 1 ? "brand" : "brands"}
        </h2>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {brands.map(b => (
          <BrandCardList key={b.slug} brand={b} />
        ))}
      </div>
    </section>
  )
}
